import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api } from '../lib/api'
import { useAuth } from '../auth/AuthContext'
import { Roles } from '../auth/rbac'

function formatDateTime(iso) {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return String(iso)
    return d.toLocaleString('ko-KR')
  } catch {
    return String(iso)
  }
}

export default function SupportInquiryDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const isOperator = useMemo(() => (user?.roles || []).includes(Roles.OPERATOR), [user?.roles])

  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')
  const [data, setData] = useState(null)

  const [reply, setReply] = useState('')
  const [replying, setReplying] = useState(false)

  useEffect(() => {
    let ignore = false
    async function load() {
      if (!id) return
      setLoading(true)
      setErr('')
      try {
        const res = await api.get(`/inquiries/${id}`)
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        if (!ignore) {
          setData(payload)
          setReply(payload?.adminReply || '')
        }
      } catch (e) {
        console.error(e)
        if (!ignore) setErr('문의 내용을 불러오지 못했습니다. (권한/존재 여부 확인)')
      } finally {
        if (!ignore) setLoading(false)
      }
    }
    load()
    return () => { ignore = true }
  }, [id])

  const onSubmitReply = async () => {
    if (!id) return
    if (!reply.trim()) return
    try {
      setReplying(true)
      await api.post(`/inquiries/${id}/reply`, { reply: reply.trim() })
      // reload
      const res = await api.get(`/inquiries/${id}`)
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      setData(payload)
    } catch (e) {
      console.error(e)
      setErr('답글 등록에 실패했습니다.')
    } finally {
      setReplying(false)
    }
  }

  if (loading) {
    return <div className="text-sm text-slate-600">불러오는 중…</div>
  }

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold">문의 상세</h1>
          <div className="text-xs text-slate-500 mt-1">비공개(작성자/운영자만 열람)</div>
        </div>
        <div className="flex items-center gap-2">
          <Link className="rounded border px-3 py-2 text-sm hover:bg-slate-50" to="/support/inquiries">목록</Link>
          <button type="button" className="rounded border px-3 py-2 text-sm hover:bg-slate-50" onClick={() => navigate(-1)}>
            뒤로
          </button>
        </div>
      </div>

      {err && <div className="text-sm text-red-600">{err}</div>}

      {data && (
        <div className="rounded border p-4 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="font-semibold">{data.title}</div>
            <div className="text-xs text-slate-500">{formatDateTime(data.createdAt)}</div>
          </div>
          <div className="whitespace-pre-wrap text-sm text-slate-800">{data.content}</div>
          <div className="text-xs text-slate-500">
            상태: {data.status === 'ANSWERED' ? '답변완료' : '접수'} · 수정일: {formatDateTime(data.updatedAt)}
          </div>
        </div>
      )}

      <div className="rounded border p-4 space-y-3">
        <div className="font-semibold text-sm">운영자 답글</div>
        {data?.adminReply ? (
          <div className="whitespace-pre-wrap text-sm text-slate-800">{data.adminReply}</div>
        ) : (
          <div className="text-sm text-slate-500">아직 답변이 없습니다.</div>
        )}
        {data?.adminRepliedAt && (
          <div className="text-xs text-slate-500">답변일: {formatDateTime(data.adminRepliedAt)}</div>
        )}

        {isOperator && (
          <div className="pt-2 border-t space-y-2">
            <div className="text-xs text-slate-500">운영자만 답글을 작성/수정할 수 있습니다.</div>
            <textarea
              className="w-full rounded border px-3 py-2 min-h-[140px]"
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="답글을 입력하세요"
            />
            <button
              type="button"
              className="rounded bg-brand-navy text-white px-4 py-2 text-sm disabled:opacity-60"
              disabled={replying || !reply.trim()}
              onClick={onSubmitReply}
            >
              {replying ? '등록 중…' : '답글 등록'}
            </button>
          </div>
        )}
      </div>
    </section>
  )
}

